import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/base/PageHeader";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { storyTimeline } from "@/mocks/storyTimeline";
import { Link } from "react-router-dom";

const highlights = [
  {
    icon: "ri-hand-heart-line",
    title: "守护与成长",
    text: "从守护挚爱到守护大陆，唐三的每一次突破都因守护而起，也让故事始终带着温度。",
  },
  {
    icon: "ri-team-line",
    title: "伙伴与情谊",
    text: "史莱克七怪并肩作战的羁绊，是贯穿全篇最动人的线索之一。",
  },
  {
    icon: "ri-sword-line",
    title: "武魂与传承",
    text: "双生武魂、宗门传承与魂师体系，构成这套独特又完整的修炼世界观。",
  },
];

export default function Story() {
  useSeo({
    title: "斗罗大陆漫画剧情进展 · 主线时间线与大结局速览",
    description:
      "斗罗大陆漫画最新剧情讲什么？主篇大结局与完整主线时间线速览，从双生武魂觉醒、史莱克七怪、魂师大赛、海神九考到成神落幕一次看明白。",
    keywords: "斗罗大陆漫画剧情,斗罗大陆漫画大结局,斗罗大陆漫画主线,斗罗大陆漫画最新剧情,斗罗大陆漫画完结",
    canonicalPath: "/story",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://dcomx.github.io/" },
          { "@type": "ListItem", position: 2, name: "剧情进展", item: "https://dcomx.github.io/story" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "斗罗大陆漫画剧情进展：主线时间线与大结局速览",
        description: "斗罗大陆漫画最新剧情、主线时间线与成神大结局速览。",
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: "斗罗漫报" },
        url: "https://dcomx.github.io/story",
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          breadcrumbs={[{ label: "首页", to: "/" }, { label: "剧情进展" }]}
          title="剧情进展 · 主线时间线速览"
          subtitle="从双生武魂觉醒到成神落幕，用一条主线理清斗罗大陆漫画的完整脉络，最新剧情与完结情况一目了然。"
          image="https://readdy.ai/api/search-image?query=Epic%20panorama%20of%20a%20cultivation%20world%20with%20floating%20islands%20linked%20by%20glowing%20paths%20and%20a%20heroic%20figure%20ascending%20toward%20divine%20light%2C%20chinese%20fantasy%2C%20warm%20crimson%20and%20amber%20tone%2C%20ink%20wash%20mixed%20with%20vibrant%20color%2C%20high%20detail&width=1600&height=640&seq=story-header&orientation=landscape"
          alt="斗罗大陆漫画剧情主线背景"
        />

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            kicker="最新剧情"
            title="主篇剧情已至成神大结局"
            desc="成神与守护为唐三的完整成长线画上句点，最新剧情速览帮你快速掌握收尾阶段。"
          />
          <article className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <img
                src="https://readdy.ai/api/search-image?query=A%20radiant%20hero%20standing%20at%20the%20summit%20of%20a%20floating%20mountain%20with%20soul%20rings%20glowing%20around%20him%2C%20divine%20golden%20beams%20breaking%20through%20clouds%2C%20chinese%20fantasy%20apotheosis%2C%20warm%20amber%20and%20cream%20tones%2C%20ink%20wash%20mixed%20with%20vibrant%20color%2C%20high%20detail&width=1200&height=720&seq=story-detail&orientation=landscape"
                alt="唐三成就海神之位，斗罗大陆漫画大结局"
                className="h-64 w-full rounded-lg object-cover md:h-80"
              />
              <h3 className="mt-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                成神与守护：最终之战如何落下帷幕
              </h3>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  历经双生武魂觉醒、史莱克七怪聚首、全大陆魂师大赛扬名、海神九考历练等一系列磨砺，故事最终走向整个主篇的高潮决战。随着最终之战的落幕，唐三成就海神之位，以更强的力量守护挚爱与大陆安宁。
                </p>
                <p>
                  这段收尾不仅是实力境界的突破，更是角色心境的圆满：少年时的隐忍与倔强，一路走来对同伴的守护、对命运的抗争，最终都在这一刻汇聚成守护的力量。
                </p>
                <p>
                  如果你正在纠结完结没有、更新到哪了，可以放心的是——本站追更话数页已把主篇整理收录至完结篇，从第一话到成神大结局的关键话数都能一路回看，追更前先看进度，不迷路。
                </p>
              </div>
            </div>

            <aside className="flex flex-col gap-4">
              {highlights.map((h) => (
                <div key={h.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500/15 text-accent-600">
                    <i className={`${h.icon} text-xl`} />
                  </span>
                  <h4 className="mt-3 font-heading text-base font-bold text-foreground-950">{h.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{h.text}</p>
                </div>
              ))}
            </aside>
          </article>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-5xl px-4 md:px-6">
            <SectionHeading
              kicker="主线时间线"
              title="六大阶段，一路看尽唐三成长"
              desc="按时间线梳理主篇剧情的关键阶段，帮助你快速建立故事全貌。"
            />
            <div className="relative mt-12">
              <div className="absolute left-[15px] top-0 h-full w-px bg-accent-200 md:left-1/2"></div>
              <div className="space-y-10">
                {storyTimeline.map((item, idx) => {
                  const left = idx % 2 === 0;
                  return (
                    <div
                      key={item.phase}
                      className={`relative flex flex-col gap-4 pl-12 md:w-1/2 md:pl-0 ${
                        left ? "md:pr-12" : "md:ml-auto md:pl-12"
                      }`}
                    >
                      <span
                        className={`absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-accent-400 bg-background-50 text-accent-600 md:left-auto ${
                          left ? "md:-right-4" : "md:-left-4"
                        }`}
                      >
                        <i className="ri-sparkling-line text-sm" />
                      </span>
                      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-bold text-primary-700">
                          {item.phase}
                        </span>
                        <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-100 p-6 sm:grid-cols-2 md:p-8">
            <div>
              <h3 className="font-heading text-xl font-bold text-foreground-950">
                想知道主篇到底讲到第几话了？
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                从追更话数页按剧情阶段速查关键话数，快速定位当前进度与后续阶段。
              </p>
            </div>
            <div className="flex items-center gap-3 sm:justify-end">
              <Link
                to="/chapters"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看追更话数
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                关于本站
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}